import { useMutation } from '@apollo/client';
import { notification } from 'antd';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import { Dispatch, SetStateAction } from 'react';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { GeneralFunction } from 'types/common';

import { INewInitalFieldsProps } from './types';

export const getBaseInputName = (name: string) => {
  const nameAsArray = name.split('-');
  if (!Number.isNaN(parseInt(nameAsArray[nameAsArray.length - 1], 10))) nameAsArray.pop();
  return nameAsArray.join('-');
};

export const getAddMoreButtonOnClickFunction = (
  sectionIndex: number,
  setSectionInputFields: Dispatch<SetStateAction<INewInitalFieldsProps[]>>,
  setValidationState: Dispatch<SetStateAction<Record<string, any>>>,
  sectionInputProps: INewInitalFieldsProps,
): (() => void) => {
  const onAddMoreButtonClick = () => {
    const nameKey = Date.now();
    const inputFieldTemplate = sectionInputProps.allowAddMoreTemplate;
    const { schemaTemplate, complexSchemaConverter } = sectionInputProps;
    setSectionInputFields((c) => {
      const current = [...c];
      if (inputFieldTemplate) {
        const newSideFieldInputNamesWithKeys = inputFieldTemplate.sideFields?.map((sF) => ({
          ...sF,
          name: `${sF.name}-${nameKey}`,
        }));
        current[sectionIndex].inputProps.splice(current[sectionIndex].inputProps.length + 1, 0, {
          ...inputFieldTemplate,
          name: `${inputFieldTemplate.name}-${nameKey}`,
          sideFields: newSideFieldInputNamesWithKeys,
        });
      }
      return current;
    });
    setValidationState((c) => {
      const current = { ...c };
      if (inputFieldTemplate) {
        const newFieldInputName = inputFieldTemplate.name;
        current[`${newFieldInputName}-${nameKey}`] = schemaTemplate?.[newFieldInputName];
        inputFieldTemplate.sideFields?.forEach((sF) => {
          current[`${sF.name}-${nameKey}`] = schemaTemplate?.[sF.name];
        });
        if (complexSchemaConverter) {
          return complexSchemaConverter(current, nameKey.toString());
        }
      }
      return current;
    });
  };
  return onAddMoreButtonClick;
};

export const getClearButtonOnClickFunction = (
  fieldIndex: number,
  sectionIndex: number,
  setSectionInputFields: Dispatch<SetStateAction<INewInitalFieldsProps[]>>,
  setValidationState: Dispatch<SetStateAction<Record<string, any>>>,
  sectionInputProps: INewInitalFieldsProps,
): (() => void) => {
  const fullInputFields = sectionInputProps.inputProps;

  const inputFieldInfo = fullInputFields[fieldIndex];

  const onClearButtonClick = () => {
    const mainFieldInputName = inputFieldInfo.name;
    const sideFieldInputNames = inputFieldInfo.sideFields ? inputFieldInfo.sideFields.map((sField) => sField.name) : [];
    setSectionInputFields((c) => {
      const current = [...c];
      current[sectionIndex].inputProps.splice(fieldIndex, 1);
      return current;
    });
    setValidationState((c) => {
      const current = { ...c };
      delete current[mainFieldInputName];
      sideFieldInputNames.forEach((sField) => delete current[sField]);
      return current;
    });
  };
  return onClearButtonClick;
};

export const useUpdateUser = (
  refetchUserData?: GeneralFunction,
  showNotificationOnSuccess = true,
  showNotificationOnFailure = true,
): any[] => {
  const { email } = useCurrentLoggedInUser(); // test nha
  const [updateUser, { loading }] = useMutation(UPDATE_USER);
  const updateUserFunction = async (value: any) => {
    try {
      await updateUser({
        variables: {
          filter: {
            email,
          },
          updatedDoc: {
            ...value,
          },
        },
      });
      console.log('updated in useUpdateUser');
      // setShowSuccess(true);
      // dispatch(
      //   UserAction.fetchUserSuccess({
      //     email,
      //     firstName: value.firstName,
      //     lastName: value.lastName,
      //   }),
      // );
      if (showNotificationOnSuccess)
        notification.success({
          message: 'Update profile successfully!',
          key: 'updateProfile',
          duration: 2,
        });
      if (refetchUserData) refetchUserData();
    } catch (err) {
      console.error(err);
      if (showNotificationOnFailure)
        notification.error({
          message: 'Update failed! Please try again.',
          key: 'updateProfile',
          duration: 2,
        });
    }
  };
  return [loading, updateUserFunction];
};
